// pageStyle is CSS added to the print document only, e.g. an @page rule that must not reach the page itself.
export function buildPrintDocument(
  content: Element,
  title: string,
  bodyClass: string,
  pageStyle = '',
): string {
  const source = content.ownerDocument;
  const printDocument = source.implementation.createHTMLDocument(title);
  printDocument.body.className = bodyClass;
  source.head
    .querySelectorAll('style, link[rel="stylesheet"]')
    .forEach((style) =>
      printDocument.head.append(printDocument.importNode(style, true)),
    );
  if (pageStyle) {
    const style = printDocument.createElement('style');
    style.textContent = pageStyle;
    printDocument.head.append(style);
  }
  printDocument.body.append(printDocument.importNode(content, true));
  return `<!doctype html>${printDocument.documentElement.outerHTML}`;
}

// print() returns before the browser's print dialog opens; the dialog announces itself with beforeprint.
const PRINT_START_TIMEOUT_MS = 5000;
// The frame loads every copied page stylesheet first, which can be slow on a cold cache.
const FRAME_LOAD_TIMEOUT_MS = 30000;

export function printElement(
  content: Element,
  title: string,
  bodyClass: string,
  pageStyle = '',
  signal?: AbortSignal,
): Promise<void> {
  const source = content.ownerDocument;
  const focused = source.activeElement as HTMLElement | null;
  // Chrome names the "Save as PDF" file after the tab's title, not the printed frame's.
  const tabDocument = topDocument(source);
  const tabTitle = tabDocument.title;
  const frame = source.createElement('iframe');
  frame.setAttribute('sandbox', 'allow-same-origin allow-modals');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.border = '0';
  frame.style.visibility = 'hidden';
  return new Promise((resolve) => {
    let started = false;
    let ended = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // The frame, the tab title and the focus stay until printing ends: removing the frame cancels the print.
    const end = (): void => {
      if (ended) {
        return;
      }
      ended = true;
      clearTimeout(timer);
      tabDocument.title = tabTitle;
      frame.remove();
      focused?.focus();
      resolve();
    };
    // Aborting drops a print whose dialog has not opened yet; an open print dialog owns the job.
    signal?.addEventListener('abort', () => !started && end(), { once: true });
    if (signal?.aborted) {
      end();
      return;
    }
    // Printing also ends when the frame never loads or the print dialog never opens.
    const endUnlessStarted = (timeout: number): void => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!started) {
          end();
        }
      }, timeout);
    };
    frame.addEventListener(
      'load',
      () => {
        clearTimeout(timer);
        if (ended) {
          return;
        }
        try {
          const frameWindow = frame.contentWindow;
          tabDocument.title = title;
          frameWindow?.addEventListener('beforeprint', () => (started = true));
          frameWindow?.addEventListener('afterprint', end);
          frameWindow?.focus();
          frameWindow?.print();
          endUnlessStarted(PRINT_START_TIMEOUT_MS);
        } catch (error) {
          console.error('Print failed', error);
          end();
        }
      },
      { once: true },
    );
    frame.srcdoc = buildPrintDocument(content, title, bodyClass, pageStyle);
    source.body.append(frame);
    endUnlessStarted(FRAME_LOAD_TIMEOUT_MS);
  });
}

function topDocument(source: Document): Document {
  try {
    return source.defaultView?.top?.document ?? source;
  } catch {
    // A cross-origin top window cannot be read.
    return source;
  }
}
